<template>
  <!--
    AudioArtwork.vue
    Cover-art layer for AUDIO episodes in the two players (MediaPlayer.vue's
    playlist player and EpisodePlayerDialog.vue's quick-play). An audio file
    has no picture for the <video> element to paint, so without this the
    player was just a black rectangle behind the controls.

    Rendered as an absolutely-positioned layer over the (empty) <video>
    surface, NOT as the video's `poster` attribute: poster handling varies by
    browser (sizing, whether it survives once playback starts, iOS inline
    behaviour), whereas a plain <img> looks identical everywhere.

    Two parts, mirroring services/artwork.js's square RSS covers so the look
    matches what podcast apps show: a blurred, dimmed, zoomed copy of the image
    filling the whole player as a backdrop, and the image itself centered,
    uncropped (object-fit: contain), so a 16:9 thumbnail and a square cover
    both read correctly without black bars.

    pointer-events: none on the whole layer, so taps/clicks fall through to the
    <video> underneath exactly as before — both players use a tap on the video
    to reveal their auto-hiding controls.
  -->
  <div class="audio-artwork" aria-hidden="true">
    <!-- Blurred backdrop: the same image, zoomed past the edges and dimmed,
         so the letterbox around the centered cover isn't flat black. -->
    <div class="audio-artwork-backdrop" :style="{ backgroundImage: `url('${artworkUrl}')` }"></div>
    <!-- The cover itself. On a load error (e.g. an expired external thumbnail
         URL) swap to the default cover instead of a broken-image icon. -->
    <img
      :src="artworkUrl"
      class="audio-artwork-img"
      alt=""
      draggable="false"
      @error="onImageError"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { resolveThumbnail } from "@/utils/thumbnail";

const props = defineProps({
  // Raw stored thumbnail value (episode.thumbnail_path): absolute URL,
  // relative FYT path, or empty — resolveThumbnail handles all three,
  // falling back to the default cover when empty.
  thumbnail: {
    type: String,
    default: null,
  },
});

// Set when the episode's own image failed to load, so we fall back to the
// default cover. Per-image state, reset whenever the thumbnail changes (the
// playlist player keeps this component mounted while advancing episodes).
const imageFailed = ref(false);
watch(
  () => props.thumbnail,
  () => {
    imageFailed.value = false;
  },
);

// The URL actually shown: the episode's image, or the default cover when it
// has none / it failed to load. resolveThumbnail(null) IS the default cover.
const artworkUrl = computed(() => resolveThumbnail(imageFailed.value ? null : props.thumbnail));

/**
 * onImageError - fall back to the default cover once. Guarded so that if the
 * default cover itself fails (API unreachable) we don't loop between the two.
 */
const onImageError = () => {
  if (!imageFailed.value) imageFailed.value = true;
};
</script>

<style scoped>
/* Fills the player's video container, above the empty <video> element
   (z-index 0 — every overlay in both players sits at 1 or higher, so the
   loading/preparing dimmers, play buttons, title and controls stay on top). */
.audio-artwork {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  pointer-events: none;
  background: #000;
}

/* Blurred backdrop. scale() pushes the blur's soft, see-through edges outside
   the clipped area; brightness keeps it clearly behind the cover and the
   white controls readable on top of a bright image. */
.audio-artwork-backdrop {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: blur(40px) brightness(0.45);
  transform: scale(1.25);
}

/* The cover. Capped so it sits clear of the title row at the top and the
   two-row controls bar at the bottom rather than disappearing under them. */
.audio-artwork-img {
  position: relative;
  max-width: min(80%, 560px);
  max-height: calc(100% - 320px);
  min-height: 120px;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
  user-select: none;
}
</style>
